import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Shield, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  project_manager: 'text-blue-400 bg-blue-900/30 border-blue-600/40',
  grade_school_editor: 'text-emerald-400 bg-emerald-900/30 border-emerald-600/40',
  theological_reviewer: 'text-purple-400 bg-purple-900/30 border-purple-600/40',
  padawan_tester: 'text-cyan-400 bg-cyan-900/30 border-cyan-600/40',
  deliverance_editor: 'text-rose-400 bg-rose-900/30 border-rose-600/40',
};

const Register = () => {
  const navigate = useNavigate();
  const { register, login } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    church_organization: '',
    purpose: ''
  });
  const [loading, setLoading] = useState(false);

  // Fetch invite info if token present
  useEffect(() => {
    if (!inviteToken) return;
    const fetchInvite = async () => {
      try {
        const res = await fetch(`${API_URL}/api/team/invite-info/${inviteToken}`);
        if (res.ok) {
          const data = await res.json();
          setInviteInfo(data);
          setFormData(prev => ({
            ...prev,
            email: data.email || '',
            name: data.name || prev.name,
          }));
        } else {
          toast.error('Invitation link is invalid or expired');
        }
      } catch {
        toast.error('Could not verify invitation');
      } finally {
        setInviteLoading(false);
      }
    };
    fetchInvite();
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const regResult = await register({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      church_organization: formData.church_organization || null,
      purpose: formData.purpose || null
    });

    if (regResult.success) {
      // If we have an invite token, login and auto-accept
      if (inviteToken) {
        const loginResult = await login(formData.email, formData.password);
        if (loginResult.success && loginResult.token) {
          try {
            const acceptRes = await fetch(`${API_URL}/api/team/auto-accept/${inviteToken}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${loginResult.token}`,
              },
              credentials: 'include',
            });
            if (acceptRes.ok) {
              const acceptData = await acceptRes.json();
              toast.success(`Welcome to the team, ${formData.name}! You're starting as a Beta Tester.`);
              navigate('/welcome-beta');
              setLoading(false);
              return;
            }
          } catch {
            // Fall through to normal flow
          }
        }
        navigate('/explore');
      } else {
        navigate('/login');
      }
    }

    setLoading(false);
  };

  if (inviteLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500 animate-pulse">Verifying invitation...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <section className="py-12 px-4">
        <div className="container mx-auto max-w-md">
          {/* Invitation Banner */}
          {inviteInfo && (
            <div className={`mb-4 p-4 rounded-lg border ${ROLE_COLORS[inviteInfo.role] || 'text-amber-400 bg-amber-900/20 border-amber-600/30'}`}
              data-testid="invite-banner">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-bold tracking-wide">You've Been Invited</span>
              </div>
              <p className="text-sm opacity-90">
                Role: <strong>{inviteInfo.role_label}</strong>
              </p>
              {inviteInfo.note && (
                <p className="text-xs opacity-70 mt-1">{inviteInfo.note}</p>
              )}
              <p className="text-xs opacity-60 mt-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Create your account to begin your journey through the curriculum.
              </p>
            </div>
          )}

          <Card>
            <CardHeader className="text-center">
              <div className="text-4xl mb-4">
                {inviteInfo ? '\u2694\uFE0F' : '\uD83D\uDCEF'}
              </div>
              <CardTitle className="text-2xl">
                {inviteInfo ? 'Join the Council' : 'Create Your Account'}
              </CardTitle>
              <CardDescription>
                {inviteInfo
                  ? `Register to begin your training as ${inviteInfo.role_label}`
                  : 'Get free access to Little Book 2 web reader and mind map'
                }
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="register-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your name"
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    data-testid="register-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    disabled={loading || !!inviteInfo}
                  />
                  {inviteInfo && (
                    <p className="text-xs text-gray-500">Email is locked to your invitation</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    data-testid="register-password"
                    type="password"
                    required
                    minLength={6}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="At least 6 characters"
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    data-testid="register-confirm-password"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    placeholder="Re-enter password"
                    disabled={loading}
                  />
                </div>

                {!inviteInfo && (
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm text-gray-600 mb-4">
                      <strong>Optional:</strong> Help us serve you better
                    </p>

                    <div className="space-y-2">
                      <Label htmlFor="church_organization">Church or Organization</Label>
                      <Input
                        id="church_organization"
                        type="text"
                        value={formData.church_organization}
                        onChange={(e) => setFormData({...formData, church_organization: e.target.value})}
                        placeholder="Grace Community Church"
                        disabled={loading}
                      />
                    </div>

                    <div className="space-y-2 mt-4">
                      <Label htmlFor="purpose">Purpose of Use</Label>
                      <Select
                        value={formData.purpose}
                        onValueChange={(value) => setFormData({...formData, purpose: value})}
                        disabled={loading}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select purpose" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ministry-leader">Ministry Leader</SelectItem>
                          <SelectItem value="church-member">Church Member</SelectItem>
                          <SelectItem value="personal-study">Personal Study</SelectItem>
                          <SelectItem value="academic">Academic/Research</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="register-submit-btn"
                  className="w-full bg-amber-600 hover:bg-amber-700 mt-6"
                  disabled={loading}
                >
                  {loading
                    ? 'Creating Account...'
                    : inviteInfo
                      ? `Join as ${inviteInfo.role_label}`
                      : 'Create Free Account'
                  }
                </Button>
              </form>
            </CardContent>

            <CardFooter className="flex-col space-y-4">
              <div className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-amber-600 hover:text-amber-700 font-medium">
                  Sign in
                </Link>
              </div>
              <div className="text-xs text-gray-500 text-center">
                By creating an account, you agree to our Terms of Service and Privacy Policy
              </div>
            </CardFooter>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Register;
